import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  Box,
  VStack,
  Text,
  Button,
  useDisclosure,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Progress,
  Flex,
  Icon,
  HStack,
  Image,
  IconButton,
} from '@chakra-ui/react'
import { motion } from 'framer-motion'
import {
  FiFolder,
  FiFileText,
  FiUploadCloud,
  FiTrash2,
  FiUser,
  FiUsers,
  FiX,
  FiSettings,
  FiMail,
  FiCreditCard,
  FiBarChart,
  FiShare2,
  FiStar,
  FiClock,
} from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import FolderTree from '@/components/FolderTree'
import CreateFolderModal from '@/components/Modals/CreateFolderModal'
import UploadModal from '@/components/Modals/UploadModal'
import { useNavigation } from '@/hooks/useNavigation'
import { useAppDispatch } from '@/store'
import { FileItem } from '@/types'
import { containerVariants, listItemVariants, buttonVariants } from '@/theme/animations'
import { selectFile } from '@/store/slices/uiSlice'
import apiService from '@/services/api'
import CoreTechLogo from '@/assets/logo.svg'
import { useAuth } from '@/contexts/AuthContext'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { Avatar } from '@chakra-ui/react'
import { MotionBox } from '@/components/Common/MotionBox'

const MotionButton = motion(Button as any)

interface SidebarProps {
  isOpen: boolean
  onClose?: () => void
  isMobile?: boolean
}

export default function Sidebar({ isOpen, onClose, isMobile = false }: SidebarProps) {
  if (!isOpen) return null

  const dispatch = useAppDispatch()
  const { user } = useAuth()
  const { t } = useAppTranslation(['common', 'dashboard'])

  // Resizable sidebar state (desktop only)
  const [sidebarWidth, setSidebarWidth] = useState(280)
  const [isResizing, setIsResizing] = useState(false)
  const sidebarRef = useRef<HTMLDivElement>(null)

  const MIN_WIDTH = 240
  const MAX_WIDTH = 500

  // Get navigation state and actions from context
  const { folderTree, isLoadingTree, treeError, selectedFolder, selectFolder, refreshFolderTree } =
    useNavigation()

  const {
    isOpen: isCreateFolderOpen,
    onOpen: onCreateFolderOpen,
    onClose: onCreateFolderClose,
  } = useDisclosure()

  const { isOpen: isUploadOpen, onOpen: onUploadOpen, onClose: onUploadClose } = useDisclosure()

  // Organization stats state
  const [organizationStats, setOrganizationStats] = useState<{
    total_file_size: string
    total_page_count: string
    total_files: number
    file_types: Record<string, { count: number; size: string }>
    ocr_status: Record<string, number>
    embedding_status: Record<string, number>
    calculated_at: string | null
    page_limits: {
      total_available: number
      current_used: number
      remaining: number
      usage_percentage: number
      is_at_limit: boolean
      plan_name: string
    }
  } | null>(null)
  const [isLoadingStats, setIsLoadingStats] = useState(false)

  const navigate = useNavigate()

  // Fetch organization stats
  useEffect(() => {
    const fetchOrganizationStats = async () => {
      try {
        setIsLoadingStats(true)
        const stats = await apiService.getOrganizationStats()
        setOrganizationStats(stats)
      } catch (error) {
        console.error('Failed to fetch organization stats:', error)
      } finally {
        setIsLoadingStats(false)
      }
    }

    fetchOrganizationStats()
  }, [])

  const handleRecentlyViewedClick = () => {
    navigate('/recently-viewed')
  }

  const handleTrashClick = () => {
    navigate('/trash')
  }

  const handleProfileClick = () => {
    navigate('/account')
  }

  const handleDashboardClick = () => {
    navigate('/dashboard')
    // Close sidebar on mobile when navigating to dashboard
    if (isMobile) {
      onClose?.()
    }
  }

  const handleAdminOrganizationsClick = () => {
    navigate('/admin/organizations')
    if (isMobile) {
      onClose?.()
    }
  }

  const handleOrganizationInvitationsClick = () => {
    navigate('/organization/invitations')
    if (isMobile) {
      onClose?.()
    }
  }

  const handleSharedWithMeClick = () => {
    navigate('/shared-with-me')
    if (isMobile) {
      onClose?.()
    }
  }

  const handleFavoritesClick = () => {
    navigate('/favorites')
    if (isMobile) {
      onClose?.()
    }
  }

  const handleGroupsClick = () => {
    navigate('/organization/groups')
    if (isMobile) {
      onClose?.()
    }
  }

  const handleAdminPlansClick = () => {
    navigate('/admin/plans')
    if (isMobile) {
      onClose?.()
    }
  }

  const handleAdminDashboardClick = () => {
    navigate('/admin/dashboard')
    if (isMobile) {
      onClose?.()
    }
  }

  const handleFolderCreated = (newFolder: any) => {
    refreshFolderTree()
    onCreateFolderClose()
  }

  const handleFileSelect = (file: FileItem) => {
    dispatch(selectFile(file))
    navigate(`/preview/${file.id}`)
    // When viewing on mobile, when a file is selected, close the sidebar
    if (isMobile) {
      onClose?.()
    }
  }

  // Page usage calculations
  const pageLimit = organizationStats?.page_limits?.total_available || 0
  const pagesUsed = organizationStats?.page_limits?.current_used || 0
  const usagePercentage = organizationStats?.page_limits?.usage_percentage || 0

  // Resize handlers (desktop only)
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (isMobile) return
      e.preventDefault()
      setIsResizing(true)
    },
    [isMobile]
  )

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing || isMobile) return

      const newWidth = e.clientX
      if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
        setSidebarWidth(newWidth)
      }
    },
    [isResizing, isMobile]
  )

  const handleMouseUp = useCallback(() => {
    setIsResizing(false)
  }, [])

  // Add event listeners for resize
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizing, handleMouseMove, handleMouseUp])

  return (
    <Box position="relative" height="100%">
      <Box
        ref={sidebarRef}
        className="electron-sidebar"
        height="100%"
        width={isMobile ? '100%' : `${sidebarWidth}px`}
        bg="gray.100"
        borderRight={isMobile ? 'none' : '1px solid'}
        borderColor="gray.200"
        overflow="auto"
        transition={isResizing ? 'none' : 'width 0.2s ease'}
        sx={{
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'transparent',
            borderRadius: '8px',
            border: '2px solid transparent',
            backgroundClip: 'padding-box',
            transition: 'background 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          },
          '&:hover::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 0, 0, 0.2)',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(0, 0, 0, 0.3)',
            transition: 'background 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
          },
          '&::-webkit-scrollbar-corner': {
            background: 'transparent',
          },
          // Firefox
          scrollbarWidth: 'thin',
          scrollbarColor: 'transparent transparent',
          '&:hover': {
            scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
          },
        }}
      >
        <MotionBox initial={false} animate="animate" variants={containerVariants}>
          <VStack spacing={6} align="stretch">
            {/* Logo Area with Close Button */}
            <MotionBox variants={listItemVariants} padding={6}>
              <HStack justify="space-between" align="start">
                <Box flex={1} cursor="pointer" onClick={handleDashboardClick}>
                  <VStack spacing={2} align="start">
                    <HStack spacing={2} align="start">
                      <Image
                        src={CoreTechLogo}
                        alt="Document Hub"
                        height="32px"
                        width="auto"
                        objectFit="contain"
                      />
                      <Text
                        fontSize="xl"
                        fontWeight="700"
                        color="gray.800"
                        letterSpacing="-0.025em"
                      >
                        Document Hub
                      </Text>
                    </HStack>
                    <Text fontSize="sm" color="gray.600" fontWeight="500">
                      {t('slogan', { ns: 'dashboard' })}
                    </Text>
                  </VStack>
                </Box>
                {isMobile && onClose && (
                  <IconButton
                    aria-label={t('buttons.close', { ns: 'common' })}
                    icon={<FiX />}
                    size="md"
                    variant="ghost"
                    color="gray.500"
                    _hover={{
                      color: 'gray.700',
                      bg: 'gray.200',
                    }}
                    onClick={onClose}
                  />
                )}
              </HStack>
            </MotionBox>

            {/* Create New Button */}
            <MotionBox variants={listItemVariants} px={6}>
              <MotionButton
                leftIcon={<FiUploadCloud />}
                w="full"
                colorScheme="brand"
                borderRadius="xl"
                boxShadow="lg"
                size="lg"
                fontWeight="600"
                onClick={onUploadOpen}
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'xl',
                }}
                transition="all 0.2s"
                bg="brand.500"
                _active={{
                  transform: 'translateY(0)',
                }}
              >
                {t('buttons.create', { ns: 'common' })}
              </MotionButton>
            </MotionBox>

            {/* Navigation Sections */}
            <VStack spacing={6} align="stretch" flex={1} px={6}>
              {/* Folders Section */}
              <MotionBox variants={listItemVariants}>
                <VStack spacing={1} align="stretch">
                  <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>
                    {t('labels.folder', { ns: 'common' }).toUpperCase()}S
                  </Text>

                  {isLoadingTree ? (
                    <Center py={4}>
                      <VStack spacing={2}>
                        <Spinner size="sm" color="brand.400" thickness="2px" />
                        <Text fontSize="xs" color="gray.600">
                          {t('status.loading', { ns: 'common' })}
                        </Text>
                      </VStack>
                    </Center>
                  ) : treeError ? (
                    <Alert status="error" size="sm" borderRadius="lg" bg="red.50" color="red.700">
                      <AlertIcon />
                      <Text fontSize="sm">{treeError}</Text>
                    </Alert>
                  ) : (
                    <VStack spacing={1} align="stretch">
                      <Button
                        variant="ghost"
                        leftIcon={<Icon as={FiFolder} />}
                        justifyContent="flex-start"
                        color="gray.700"
                        _hover={{
                          bg: 'gray.200',
                          color: 'gray.900',
                        }}
                        borderRadius="lg"
                        transition="colors 0.15s"
                        onClick={onCreateFolderOpen}
                      >
                        {t('fileExplorer.createFolder', { ns: 'files' })}
                      </Button>
                      <Box color="gray.700">
                        <FolderTree
                          folders={folderTree}
                          selectedFolder={selectedFolder}
                          onFolderSelect={selectFolder}
                          onFileSelect={handleFileSelect}
                        />
                      </Box>
                    </VStack>
                  )}
                </VStack>
              </MotionBox>

              {/* Recently viewed / Shared with me / Favourites — one nav group.
                  Recently viewed links to its page rather than listing files
                  inline, which duplicated /recently-viewed. */}
              <MotionBox variants={listItemVariants}>
                <VStack spacing={1} align="stretch">
                  <Button
                    variant="ghost"
                    leftIcon={<Icon as={FiClock} />}
                    justifyContent="flex-start"
                    color="gray.700"
                    _hover={{ bg: 'gray.200', color: 'gray.900' }}
                    borderRadius="lg"
                    transition="colors 0.15s"
                    onClick={handleRecentlyViewedClick}
                    fontSize="sm"
                  >
                    {t('navigation.recentlyViewed', { ns: 'common' })}
                  </Button>
                  <Button
                    variant="ghost"
                    leftIcon={<Icon as={FiShare2} />}
                    justifyContent="flex-start"
                    color="gray.700"
                    _hover={{ bg: 'gray.200', color: 'gray.900' }}
                    borderRadius="lg"
                    transition="colors 0.15s"
                    onClick={handleSharedWithMeClick}
                    fontSize="sm"
                  >
                    {t('navigation.sharedWithMe', { ns: 'common' })}
                  </Button>
                  <Button
                    variant="ghost"
                    leftIcon={<Icon as={FiStar} />}
                    justifyContent="flex-start"
                    color="gray.700"
                    _hover={{ bg: 'gray.200', color: 'gray.900' }}
                    borderRadius="lg"
                    transition="colors 0.15s"
                    onClick={handleFavoritesClick}
                    fontSize="sm"
                  >
                    {t('navigation.favorites', { ns: 'common' })}
                  </Button>
                </VStack>
              </MotionBox>

              {/* Admin Section - Only show for admin/super users */}
              {(user?.role === 'admin' || user?.role === 'super') && (
                <MotionBox variants={listItemVariants}>
                  <VStack spacing={1} align="stretch">
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>
                      {user?.role === 'super'
                        ? t('navigation.admin', { ns: 'common' }).toUpperCase()
                        : t('labels.organization', { ns: 'common' }).toUpperCase()}
                    </Text>

                    {user?.role === 'super' && (
                      <>
                        <Button
                          variant="ghost"
                          leftIcon={<Icon as={FiBarChart} />}
                          justifyContent="flex-start"
                          color="gray.700"
                          _hover={{
                            bg: 'gray.200',
                            color: 'gray.900',
                          }}
                          borderRadius="lg"
                          transition="colors 0.15s"
                          onClick={handleAdminDashboardClick}
                          fontSize="sm"
                        >
                          Dashboard
                        </Button>
                        <Button
                          variant="ghost"
                          leftIcon={<Icon as={FiSettings} />}
                          justifyContent="flex-start"
                          color="gray.700"
                          _hover={{
                            bg: 'gray.200',
                            color: 'gray.900',
                          }}
                          borderRadius="lg"
                          transition="colors 0.15s"
                          onClick={handleAdminOrganizationsClick}
                          fontSize="sm"
                        >
                          {t('buttons.manage', { ns: 'common' })}{' '}
                          {t('navigation.organizations', { ns: 'common' })}
                        </Button>
                        <Button
                          variant="ghost"
                          leftIcon={<Icon as={FiCreditCard} />}
                          justifyContent="flex-start"
                          color="gray.700"
                          _hover={{
                            bg: 'gray.200',
                            color: 'gray.900',
                          }}
                          borderRadius="lg"
                          transition="colors 0.15s"
                          onClick={handleAdminPlansClick}
                          fontSize="sm"
                        >
                          {t('buttons.manage', { ns: 'common' })}{' '}
                          {t('navigation.plans', { ns: 'common' })}
                        </Button>
                      </>
                    )}

                    <Button
                      variant="ghost"
                      leftIcon={<Icon as={FiMail} />}
                      justifyContent="flex-start"
                      color="gray.700"
                      _hover={{
                        bg: 'gray.200',
                        color: 'gray.900',
                      }}
                      borderRadius="lg"
                      transition="colors 0.15s"
                      onClick={handleOrganizationInvitationsClick}
                      fontSize="sm"
                    >
                      {t('navigation.invitations', { ns: 'common' })}
                    </Button>
                    <Button
                      variant="ghost"
                      leftIcon={<Icon as={FiUsers} />}
                      justifyContent="flex-start"
                      color="gray.700"
                      _hover={{
                        bg: 'gray.200',
                        color: 'gray.900',
                      }}
                      borderRadius="lg"
                      transition="colors 0.15s"
                      onClick={handleGroupsClick}
                      fontSize="sm"
                    >
                      {t('navigation.groups', { ns: 'common' })}
                    </Button>
                  </VStack>
                </MotionBox>
              )}

              {/* Trash Section */}
              <MotionBox variants={listItemVariants}>
                <VStack spacing={1} align="stretch">
                  <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>
                    {t('navigation.trash', { ns: 'common' }).toUpperCase()}
                  </Text>
                  <Button
                    variant="ghost"
                    leftIcon={<Icon as={FiTrash2} />}
                    justifyContent="flex-start"
                    color="gray.700"
                    _hover={{
                      bg: 'gray.200',
                      color: 'gray.900',
                    }}
                    borderRadius="lg"
                    transition="colors 0.15s"
                    onClick={handleTrashClick}
                    fontSize="sm"
                  >
                    {t('buttons.view', { ns: 'common' })} {t('trash.title', { ns: 'files' })}
                  </Button>
                </VStack>
              </MotionBox>
            </VStack>

            {/* Storage Indicator */}
            <MotionBox variants={listItemVariants} px={6}>
              <Box bg="gray.200" borderRadius="lg" p={4}>
                <VStack spacing={3} align="stretch">
                  <Flex align="center" gap={2}>
                    <Icon as={FiFileText} color="gray.600" />
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                      {t('storage.title', { ns: 'dashboard' })}
                    </Text>
                    {isLoadingStats && <Spinner size="xs" color="gray.600" />}
                  </Flex>
                  <Progress
                    value={usagePercentage}
                    borderRadius="full"
                    colorScheme={
                      usagePercentage > 90 ? 'red' : usagePercentage > 75 ? 'yellow' : 'brand'
                    }
                    bg="gray.300"
                    size="sm"
                  />
                  <Text fontSize="xs" color="gray.600">
                    {pagesUsed.toLocaleString()}/{pageLimit.toLocaleString()}{' '}
                    {t('storage.used', { ns: 'dashboard' })}
                  </Text>
                </VStack>
              </Box>
            </MotionBox>

            {/* Profile Section */}
            <MotionBox variants={listItemVariants} px={6} pb={6}>
              <Button
                variant="ghost"
                justifyContent="flex-start"
                color="gray.700"
                _hover={{
                  bg: 'gray.200',
                  color: 'gray.900',
                }}
                borderRadius="lg"
                transition="colors 0.15s"
                onClick={handleProfileClick}
                fontSize="sm"
                h="auto"
                py={3}
                px={3}
                w="full"
              >
                <HStack spacing={3} w="full" minW={0}>
                  <Avatar
                    size="sm"
                    name={user?.name || 'User'}
                    bg="brand.500"
                    color="white"
                    icon={<FiUser />}
                  />
                  <VStack spacing={0} align="start" flex={1} minW={0} maxW="full">
                    <Text
                      fontSize="sm"
                      fontWeight="medium"
                      color="gray.800"
                      noOfLines={1}
                      maxW="full"
                    >
                      {user?.name || 'User'}
                    </Text>
                    <Text
                      fontSize="xs"
                      color="gray.500"
                      noOfLines={1}
                      maxW="full"
                      title={user?.email}
                    >
                      {user?.email || 'n/a'}
                    </Text>
                  </VStack>
                </HStack>
              </Button>
            </MotionBox>
          </VStack>
        </MotionBox>

        {/* Modals */}
        <CreateFolderModal
          isOpen={isCreateFolderOpen}
          onClose={onCreateFolderClose}
          onFolderCreated={handleFolderCreated}
          parentFolder={null}
        />
        <UploadModal isOpen={isUploadOpen} onClose={onUploadClose} targetFolder={null} />
      </Box>
      {/* Resize Handle (Desktop only) */}
      {!isMobile && (
        <Box
          position="absolute"
          top="0"
          right="0"
          width="4px"
          height="100vh"
          cursor="col-resize"
          bg="transparent"
          _hover={{
            bg: 'brand.400',
          }}
          transition="background 0.2s ease"
          onMouseDown={handleMouseDown}
          zIndex="10"
        />
      )}
    </Box>
  )
}
